/**
 * Configuração completa de tipos de documentos jurídicos
 * 
 * Estrutura hierárquica:
 * - Categoria (Peças Jurídicas, Contrato, Extrajudicial, Ferramentas)
 *   - Área (Cível, Trabalhista, Criminal, etc.)
 *     - Tipo de Documento
 *       - Campos específicos
 */

// ==========================================
// INTERFACES
// ==========================================

export interface DocumentField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'date' | 'number' | 'currency' | 'file' | 'checkbox';
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: { value: string; label: string }[];
  maxLength?: number;
  minLength?: number;
  validation?: {
    pattern?: string;
    message?: string;
  };
}

export interface DocumentType {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category: DocumentCategory;
  area: DocumentArea;
  fields: DocumentField[];
  popular?: boolean; // Marca como "mais utilizado"
  order?: number; // Ordem de exibição
}

export type DocumentCategory = 
  | 'pecas_juridicas' 
  | 'contrato' 
  | 'extrajudicial' 
  | 'ferramentas_auxiliares';

export type DocumentArea = 
  | 'civel' 
  | 'trabalhista' 
  | 'criminal' 
  | 'previdenciario' 
  | 'transito' 
  | 'magistratura' 
  | 'recesso'
  | 'familia'
  | 'ambiental'
  | 'geral';

export interface CategoryConfig {
  id: DocumentCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface AreaConfig {
  id: DocumentArea;
  name: string;
  shortName: string;
  color: string;
  icon: string;
}

// ==========================================
// CONFIGURAÇÕES DE CATEGORIAS
// ==========================================

export const CATEGORIES: CategoryConfig[] = [
  {
    id: 'pecas_juridicas',
    name: 'Peças Jurídicas',
    description: 'Petições, recursos, contestações e demais peças processuais',
    icon: 'description',
    color: '#1a365d'
  },
  {
    id: 'contrato',
    name: 'Contratos',
    description: 'Elaboração, revisão e análise de contratos',
    icon: 'article',
    color: '#059669'
  },
  {
    id: 'extrajudicial',
    name: 'Extrajudicial',
    description: 'Documentos e procedimentos fora do âmbito judicial',
    icon: 'folder',
    color: '#0891b2'
  },
  {
    id: 'ferramentas_auxiliares',
    name: 'Ferramentas Auxiliares',
    description: 'Busca de jurisprudência, cálculos, resumos e mais',
    icon: 'build',
    color: '#ea580c'
  }
];

// ==========================================
// CONFIGURAÇÕES DE ÁREAS
// ==========================================

export const AREAS: AreaConfig[] = [
  { id: 'civel', name: 'Cível', shortName: 'Cível', color: '#1a365d', icon: 'gavel' },
  { id: 'trabalhista', name: 'Trabalhista', shortName: 'Trab.', color: '#744210', icon: 'work' },
  { id: 'criminal', name: 'Criminal/Penal', shortName: 'Penal', color: '#1d4ed8', icon: 'security' },
  { id: 'previdenciario', name: 'Previdenciário', shortName: 'Prev.', color: '#059669', icon: 'health_and_safety' },
  { id: 'transito', name: 'Trânsito', shortName: 'Trâns.', color: '#ca8a04', icon: 'directions_car' },
  { id: 'magistratura', name: 'Magistratura', shortName: 'Mag.', color: '#0891b2', icon: 'account_balance' },
  { id: 'familia', name: 'Família', shortName: 'Fam.', color: '#db2777', icon: 'family_restroom' },
  { id: 'ambiental', name: 'Ambiental', shortName: 'Amb.', color: '#16a34a', icon: 'park' },
  { id: 'recesso', name: 'Recesso', shortName: 'Rec.', color: '#ea580c', icon: 'event' },
  { id: 'geral', name: 'Geral', shortName: 'Geral', color: '#6b7280', icon: 'folder' }
];

// ==========================================
// CAMPOS PADRÃO REUTILIZÁVEIS
// ==========================================

const CAMPOS_PADRAO = {
  nomeCliente: {
    id: 'nome_cliente',
    name: 'nome_cliente',
    label: 'Nome do seu cliente',
    type: 'text' as const,
    required: true,
    placeholder: 'Ex: O autor é Igor Pereira Severino.',
    helpText: "Separe os nomes com ';' caso você represente mais de uma parte"
  },
  fatosDosCaso: {
    id: 'fatos_caso',
    name: 'fatos_caso',
    label: 'Quais são os fatos do caso?',
    type: 'textarea' as const,
    required: true,
    placeholder: 'Descreva detalhadamente os fatos que fundamentam a ação...',
    helpText: 'Seja o mais detalhado possível para que a IA possa gerar um documento preciso',
    maxLength: 10000
  },
  parteContraria: {
    id: 'parte_contraria',
    name: 'parte_contraria',
    label: 'Nome da parte contrária',
    type: 'text' as const,
    required: true,
    placeholder: 'Ex: O réu é Manuel Pereira.'
  },
  valorCausa: {
    id: 'valor_causa',
    name: 'valor_causa',
    label: 'Valor da causa',
    type: 'currency' as const,
    required: false,
    placeholder: 'R$ 0,00'
  },
  documentosAnexos: {
    id: 'documentos_anexos',
    name: 'documentos_anexos',
    label: 'Envio de documentos (Opcional)',
    type: 'file' as const,
    required: false,
    helpText: 'Anexe documentos relevantes para o caso'
  },
  estiloEscrita: {
    id: 'estilo_escrita',
    name: 'estilo_escrita',
    label: 'Estilo de escrita',
    type: 'select' as const,
    required: false,
    options: [
      { value: 'padrao', label: 'Padrão Jurídico AI' },
      { value: 'formal', label: 'Formal Tradicional' },
      { value: 'objetivo', label: 'Objetivo e Conciso' }
    ],
    helpText: 'Escolha o estilo de escrita que a IA deve utilizar'
  },
  // Campos específicos para contestação
  numeroProcesso: {
    id: 'numero_processo',
    name: 'numero_processo',
    label: 'Número do processo',
    type: 'text' as const,
    required: true,
    placeholder: 'Ex: 0000000-00.0000.0.00.0000'
  },
  // Campos específicos para recursos
  dataIntimacao: {
    id: 'data_intimacao',
    name: 'data_intimacao',
    label: 'Data da intimação',
    type: 'date' as const,
    required: false
  },
  fundamentosRecurso: {
    id: 'fundamentos_recurso',
    name: 'fundamentos_recurso',
    label: 'Fundamentos do recurso',
    type: 'textarea' as const,
    required: true,
    placeholder: 'Descreva os fundamentos legais e fáticos para o recurso...',
    maxLength: 10000
  },
  // Campos específicos para habeas corpus
  nomeAutoridade: {
    id: 'nome_autoridade',
    name: 'nome_autoridade',
    label: 'Autoridade coatora',
    type: 'text' as const,
    required: true,
    placeholder: 'Nome da autoridade responsável pela coação'
  },
  nomePaciente: {
    id: 'nome_paciente',
    name: 'nome_paciente',
    label: 'Nome do paciente',
    type: 'text' as const,
    required: true,
    placeholder: 'Nome da pessoa que sofre a coação'
  }
};

// ==========================================
// CONFIGURAÇÃO DE CAMPOS POR TIPO
// ==========================================

// Campos padrão para a maioria das peças
const CAMPOS_PETICAO_INICIAL: DocumentField[] = [
  CAMPOS_PADRAO.nomeCliente,
  CAMPOS_PADRAO.fatosDosCaso,
  CAMPOS_PADRAO.parteContraria,
  CAMPOS_PADRAO.valorCausa,
  CAMPOS_PADRAO.documentosAnexos,
  CAMPOS_PADRAO.estiloEscrita
];

const CAMPOS_CONTESTACAO: DocumentField[] = [
  CAMPOS_PADRAO.numeroProcesso,
  CAMPOS_PADRAO.nomeCliente,
  CAMPOS_PADRAO.parteContraria,
  CAMPOS_PADRAO.fatosDosCaso,
  CAMPOS_PADRAO.documentosAnexos,
  CAMPOS_PADRAO.estiloEscrita
];

const CAMPOS_RECURSO: DocumentField[] = [
  CAMPOS_PADRAO.numeroProcesso,
  CAMPOS_PADRAO.nomeCliente,
  CAMPOS_PADRAO.parteContraria,
  CAMPOS_PADRAO.dataIntimacao,
  CAMPOS_PADRAO.fundamentosRecurso,
  CAMPOS_PADRAO.documentosAnexos,
  CAMPOS_PADRAO.estiloEscrita
];

const CAMPOS_HABEAS_CORPUS: DocumentField[] = [
  CAMPOS_PADRAO.nomePaciente,
  CAMPOS_PADRAO.nomeAutoridade,
  CAMPOS_PADRAO.fatosDosCaso,
  CAMPOS_PADRAO.documentosAnexos,
  CAMPOS_PADRAO.estiloEscrita
];

// ==========================================
// TIPOS DE DOCUMENTOS - PEÇAS JURÍDICAS CÍVEIS
// ==========================================

export const DOCUMENT_TYPES: DocumentType[] = [
  // ========== CÍVEL - Destaques ==========
  {
    id: 'peticao_inicial_civel',
    name: 'Petição Inicial',
    description: 'Peça inaugural de processo judicial',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 1
  },
  {
    id: 'contestacao_civel',
    name: 'Contestação',
    description: 'Defesa do réu em processo cível',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 2
  },
  {
    id: 'replica_civel',
    name: 'Impugnação à Contestação (Réplica)',
    description: 'Manifestação do autor sobre a contestação',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 3
  },
  {
    id: 'manifestacao_processual',
    name: 'Manifestação Processual',
    description: 'Manifestação genérica nos autos',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 4
  },
  {
    id: 'alegacoes_finais',
    name: 'Alegações Finais',
    description: 'Razões finais antes da sentença',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 5
  },
  {
    id: 'agravo_instrumento',
    name: 'Agravo de Instrumento',
    description: 'Recurso contra decisões interlocutórias',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 6
  },
  {
    id: 'agravo_interno',
    name: 'Agravo Interno',
    description: 'Recurso contra decisão monocrática',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 7
  },
  {
    id: 'recurso_especial',
    name: 'Recurso Especial',
    description: 'Recurso ao STJ',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 8
  },
  {
    id: 'embargos_declaracao',
    name: 'Embargos de Declaração',
    description: 'Sanar omissão, obscuridade ou contradição',
    icon: '❓',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 9
  },
  {
    id: 'cumprimento_sentenca',
    name: 'Cumprimento de Sentença',
    description: 'Execução de título judicial',
    icon: '✅',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 10
  },
  {
    id: 'apelacao',
    name: 'Apelação',
    description: 'Recurso contra sentença',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 11
  },
  {
    id: 'embargos_terceiro',
    name: 'Embargos de Terceiro',
    description: 'Proteção de bem de terceiro',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 12
  },

  // ========== CÍVEL - Outras peças ==========
  {
    id: 'tutela_provisoria_cautelar',
    name: 'Tutela Provisória Cautelar Antecedente',
    description: 'Medida cautelar antes do processo principal',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 20
  },
  {
    id: 'tutela_provisoria_antecipada',
    name: 'Tutela Provisória Antecipada em Caráter Antecedente',
    description: 'Antecipação de tutela antes do processo principal',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 21
  },
  {
    id: 'adjudicacao_compulsoria',
    name: 'Inicial de Ação de Adjudicação Compulsória',
    description: 'Ação para transferência forçada de imóvel',
    icon: '🏠',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 22
  },
  {
    id: 'sustacao_protesto',
    name: 'Ação Cautelar de Sustação de Protesto',
    description: 'Suspensão de protesto de título',
    icon: '🚫',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 23
  },
  {
    id: 'busca_apreensao_menor',
    name: 'Ação de Busca e Apreensão de Menor com Tutela de Urgência',
    description: 'Busca e apreensão de criança',
    icon: '👶',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 24
  },
  {
    id: 'emenda_inicial',
    name: 'Emenda à Inicial',
    description: 'Correção de petição inicial',
    icon: '✏️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 25
  },
  {
    id: 'recuperacao_judicial',
    name: 'Pedido de Recuperação Judicial',
    description: 'Recuperação judicial de empresa',
    icon: '💼',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 26
  },
  {
    id: 'execucao_titulo_extrajudicial',
    name: 'Inicial de Ação de Execução de Título Extrajudicial',
    description: 'Execução de título extrajudicial',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 27
  },
  {
    id: 'mandado_seguranca',
    name: 'Mandado de Segurança',
    description: 'Proteção de direito líquido e certo',
    icon: '🔐',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 28
  },
  {
    id: 'quesitos_periciais',
    name: 'Manifestação para Apresentação de Quesitos Periciais',
    description: 'Quesitos para perícia técnica',
    icon: '🔬',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 29
  },
  {
    id: 'producao_provas',
    name: 'Manifestação para Produção de Provas',
    description: 'Especificação de provas a produzir',
    icon: '🔍',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 30
  },
  {
    id: 'contrarrazoes_apelacao',
    name: 'Contrarrazões de Apelação',
    description: 'Resposta à apelação da parte contrária',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 31
  },
  {
    id: 'recurso_inominado',
    name: 'Recurso Inominado',
    description: 'Recurso em Juizado Especial',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 32
  },
  {
    id: 'contrarrazoes_recurso_inominado',
    name: 'Contrarrazões ao Recurso Inominado',
    description: 'Resposta ao recurso inominado',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 33
  },
  {
    id: 'contraminuta_agravo_instrumento',
    name: 'Contraminuta ao Agravo de Instrumento',
    description: 'Resposta ao agravo de instrumento',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 34
  },
  {
    id: 'contraminuta_agravo_interno',
    name: 'Contraminuta ao Agravo Interno',
    description: 'Resposta ao agravo interno',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 35
  },
  {
    id: 'recurso_ordinario_constitucional',
    name: 'Recurso Ordinário Constitucional',
    description: 'Recurso ao STJ/STF em matéria constitucional',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 36
  },
  {
    id: 'memoriais',
    name: 'Memoriais Recursais',
    description: 'Memoriais para sustentação oral',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 37
  },
  {
    id: 'memoriais_stj',
    name: 'Memoriais Recursais para Ministro do STJ',
    description: 'Memoriais direcionados a Ministro do STJ',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 38
  },
  {
    id: 'contrarrazoes_recurso_especial',
    name: 'Contrarrazões ao Recurso Especial',
    description: 'Resposta ao recurso especial',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 39
  },
  {
    id: 'agravo_recurso_especial',
    name: 'Agravo em Recurso Especial',
    description: 'Agravo contra inadmissão de REsp',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 40
  },
  {
    id: 'contraminuta_agravo_resp',
    name: 'Contraminuta ao Agravo em Recurso Especial',
    description: 'Resposta ao agravo em REsp',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 41
  },
  {
    id: 'recurso_extraordinario',
    name: 'Recurso Extraordinário',
    description: 'Recurso ao STF',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 42
  },
  {
    id: 'contrarrazoes_re',
    name: 'Contrarrazões ao Recurso Extraordinário',
    description: 'Resposta ao recurso extraordinário',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 43
  },
  {
    id: 'embargos_divergencia',
    name: 'Embargos de Divergência',
    description: 'Uniformização de jurisprudência nos tribunais superiores',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 44
  },
  {
    id: 'contrarrazoes_embargos_declaracao',
    name: 'Contrarrazões aos Embargos de Declaração',
    description: 'Resposta aos embargos de declaração',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_RECURSO,
    order: 45
  },
  {
    id: 'impugnacao_cumprimento_sentenca',
    name: 'Impugnação ao Cumprimento de Sentença',
    description: 'Defesa na fase de execução',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 46
  },
  {
    id: 'embargos_execucao',
    name: 'Embargos à Execução',
    description: 'Defesa em execução de título extrajudicial',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 47
  },
  {
    id: 'impugnacao_embargos_execucao',
    name: 'Impugnação aos Embargos à Execução',
    description: 'Resposta aos embargos à execução',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 48
  },
  {
    id: 'excecao_pre_executividade',
    name: 'Exceção de Pré-executividade',
    description: 'Defesa sem garantia do juízo',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 49
  },
  {
    id: 'impugnacao_penhora',
    name: 'Impugnação à Penhora',
    description: 'Questionamento da penhora realizada',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 50
  },
  {
    id: 'acao_rescisoria',
    name: 'Ação Rescisória',
    description: 'Desconstituição de decisão transitada em julgado',
    icon: '🔄',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 51
  },
  {
    id: 'acordo_judicial',
    name: 'Acordo Judicial e Pedido de Extinção do Processo',
    description: 'Acordo entre as partes no processo',
    icon: '🤝',
    category: 'pecas_juridicas',
    area: 'civel',
    fields: CAMPOS_CONTESTACAO,
    order: 52
  },

  // ========== TRABALHISTA ==========
  {
    id: 'reclamacao_trabalhista',
    name: 'Reclamação Trabalhista',
    description: 'Ação perante a Justiça do Trabalho',
    icon: '👷',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 1
  },
  {
    id: 'contestacao_trabalhista',
    name: 'Contestação Trabalhista',
    description: 'Defesa do empregador',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 2
  },
  {
    id: 'replica_trabalhista',
    name: 'Impugnação à Contestação Trabalhista (Réplica)',
    description: 'Manifestação do reclamante sobre a contestação',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 3
  },
  {
    id: 'agravo_peticao',
    name: 'Agravo de Petição',
    description: 'Recurso em execução trabalhista',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 4
  },
  {
    id: 'recurso_ordinario_trabalhista',
    name: 'Recurso Ordinário Trabalhista',
    description: 'Recurso contra sentença trabalhista',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 5
  },
  {
    id: 'contrarrazoes_ro_trabalhista',
    name: 'Contrarrazões ao Recurso Ordinário Trabalhista',
    description: 'Resposta ao recurso ordinário',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 6
  },
  {
    id: 'recurso_revista',
    name: 'Recurso de Revista',
    description: 'Recurso ao TST',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 7
  },
  {
    id: 'contrarrazoes_recurso_revista',
    name: 'Contrarrazões ao Recurso de Revista',
    description: 'Resposta ao recurso de revista',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 8
  },
  {
    id: 'acordo_extrajudicial_trabalhista',
    name: 'Acordo Extrajudicial Trabalhista',
    description: 'Acordo para homologação judicial',
    icon: '🤝',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 9
  },

  // ========== CRIMINAL ==========
  {
    id: 'habeas_corpus',
    name: 'Habeas Corpus',
    description: 'Proteção à liberdade de locomoção',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_HABEAS_CORPUS,
    popular: true,
    order: 1
  },
  {
    id: 'queixa_crime',
    name: 'Queixa-Crime',
    description: 'Ação penal privada',
    icon: '📣',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 2
  },
  {
    id: 'defesa_previa_criminal',
    name: 'Defesa Prévia Criminal',
    description: 'Defesa preliminar do acusado',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 3
  },
  {
    id: 'resposta_acusacao',
    name: 'Resposta à Acusação',
    description: 'Defesa após recebimento da denúncia',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 4
  },
  {
    id: 'liberdade_provisoria',
    name: 'Pedido de Liberdade Provisória',
    description: 'Liberdade com ou sem fiança',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_HABEAS_CORPUS,
    popular: true,
    order: 5
  },
  {
    id: 'recurso_sentido_estrito',
    name: 'Recurso em Sentido Estrito',
    description: 'Recurso contra decisões interlocutórias penais',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 6
  },
  {
    id: 'revisao_criminal',
    name: 'Revisão Criminal',
    description: 'Revisão de condenação transitada em julgado',
    icon: '🔄',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 7
  },
  {
    id: 'alegacoes_finais_criminais',
    name: 'Alegações Finais Criminais',
    description: 'Razões finais em processo criminal',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 8
  },
  {
    id: 'apelacao_criminal',
    name: 'Apelação Criminal',
    description: 'Recurso contra sentença penal',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 9
  },
  {
    id: 'pedido_progressao_regime',
    name: 'Pedido de Progressão de Regime',
    description: 'Progressão de regime prisional',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 10
  },

  // ========== PREVIDENCIÁRIO ==========
  {
    id: 'concessao_beneficio',
    name: 'Inicial de Ação de Concessão de Benefício Previdenciário',
    description: 'Ação para concessão de benefício do INSS',
    icon: '🏥',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 1
  },
  {
    id: 'revisao_beneficio',
    name: 'Ação de Revisão de Benefício Previdenciário',
    description: 'Revisão de valor ou cálculo de benefício',
    icon: '🔄',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 2
  },
  {
    id: 'recurso_especial_previdenciario',
    name: 'Recurso Especial Previdenciário (CRPS/INSS)',
    description: 'Recurso administrativo ao CRPS',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 3
  },
  {
    id: 'recurso_ordinario_previdenciario',
    name: 'Recurso Ordinário Previdenciário (CRPS/INSS)',
    description: 'Recurso administrativo ordinário',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 4
  },

  // ========== TRÂNSITO ==========
  {
    id: 'defesa_previa_detran',
    name: 'Defesa Prévia (DETRAN)',
    description: 'Defesa administrativa contra autuação',
    icon: '🚗',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 1
  },
  {
    id: 'recurso_jari',
    name: 'Recurso à JARI',
    description: 'Recurso à Junta Administrativa de Recursos de Infrações',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 2
  },
  {
    id: 'recurso_cetran',
    name: 'Recurso ao CETRAN',
    description: 'Recurso ao Conselho Estadual de Trânsito',
    icon: '📤',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    popular: true,
    order: 3
  },

  // ========== MAGISTRATURA ==========
  {
    id: 'acordao',
    name: 'Acórdão',
    description: 'Decisão colegiada de tribunal',
    icon: '👨‍⚖️',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 1
  },
  {
    id: 'decisao_judicial',
    name: 'Decisão Judicial',
    description: 'Decisão interlocutória ou monocrática',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 2
  },
  {
    id: 'sentenca',
    name: 'Sentença',
    description: 'Decisão que encerra o processo em primeiro grau',
    icon: '📜',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    popular: true,
    order: 3
  },

  // ========== CONTRATOS ==========
  {
    id: 'elaborar_contrato',
    name: 'Elaborar Contrato',
    description: 'Criação de contrato personalizado',
    icon: '📝',
    category: 'contrato',
    area: 'geral',
    fields: [
      {
        id: 'tipo_contrato',
        name: 'tipo_contrato',
        label: 'Tipo de Contrato',
        type: 'select',
        required: true,
        options: [
          { value: 'prestacao_servicos', label: 'Prestação de Serviços' },
          { value: 'compra_venda', label: 'Compra e Venda' },
          { value: 'locacao', label: 'Locação' },
          { value: 'parceria', label: 'Parceria' },
          { value: 'trabalho', label: 'Trabalho' },
          { value: 'social', label: 'Contrato Social' },
          { value: 'outro', label: 'Outro' }
        ]
      },
      {
        id: 'partes_contrato',
        name: 'partes_contrato',
        label: 'Partes do Contrato',
        type: 'textarea',
        required: true,
        placeholder: 'Informe as partes envolvidas no contrato...'
      },
      {
        id: 'objeto_contrato',
        name: 'objeto_contrato',
        label: 'Objeto do Contrato',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o objeto do contrato...'
      },
      CAMPOS_PADRAO.documentosAnexos,
      CAMPOS_PADRAO.estiloEscrita
    ],
    popular: true,
    order: 1
  },
  {
    id: 'revisar_contrato',
    name: 'Revisar Contrato',
    description: 'Análise e revisão de contrato existente',
    icon: '🔍',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'pontos_atencao',
        name: 'pontos_atencao',
        label: 'Pontos de atenção',
        type: 'textarea',
        required: false,
        placeholder: 'Há algum ponto específico que deseja analisar?'
      }
    ],
    popular: true,
    order: 2
  },
  {
    id: 'analise_contrato_parecer',
    name: 'Análise de Contrato com Parecer',
    description: 'Parecer jurídico sobre contrato',
    icon: '📋',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'objetivo_analise',
        name: 'objetivo_analise',
        label: 'Objetivo da análise',
        type: 'textarea',
        required: true,
        placeholder: 'Qual o objetivo desta análise?'
      }
    ],
    order: 3
  },
  {
    id: 'criar_aditivo',
    name: 'Criar Aditivo',
    description: 'Aditivo contratual',
    icon: '➕',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'alteracoes_aditivo',
        name: 'alteracoes_aditivo',
        label: 'Alterações do aditivo',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva as alterações a serem feitas no contrato original...'
      }
    ],
    order: 4
  },
  {
    id: 'termo_encerramento',
    name: 'Criar Termo de Encerramento',
    description: 'Término de contrato',
    icon: '🔚',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'motivo_encerramento',
        name: 'motivo_encerramento',
        label: 'Motivo do encerramento',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o motivo do encerramento do contrato...'
      }
    ],
    order: 5
  },
  {
    id: 'contrato_modelo',
    name: 'Criar Contrato a Partir de Modelo',
    description: 'Adaptar um modelo de contrato existente',
    icon: '📄',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'adaptacoes',
        name: 'adaptacoes',
        label: 'Adaptações necessárias',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva as adaptações que precisam ser feitas no modelo...'
      }
    ],
    order: 6
  },
  {
    id: 'analise_negociacao',
    name: 'Análise de Negociação Contratual',
    description: 'Análise de propostas e contrapropostas',
    icon: '🤝',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'objetivo_negociacao',
        name: 'objetivo_negociacao',
        label: 'Objetivo da negociação',
        type: 'textarea',
        required: true,
        placeholder: 'Quais são os principais objetivos da negociação?'
      }
    ],
    order: 7
  },
  {
    id: 'analise_risco_rescisao',
    name: 'Análise de Risco de Rescisão',
    description: 'Avaliação de riscos de rescisão contratual',
    icon: '⚠️',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'motivo_rescisao',
        name: 'motivo_rescisao',
        label: 'Motivo pretendido para rescisão',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o motivo pelo qual deseja rescindir o contrato...'
      }
    ],
    order: 8
  },
  {
    id: 'comparar_contratos',
    name: 'Comparar Contratos',
    description: 'Comparação detalhada entre contratos',
    icon: '⚖️',
    category: 'contrato',
    area: 'geral',
    fields: [
      CAMPOS_PADRAO.documentosAnexos,
      {
        id: 'foco_comparacao',
        name: 'foco_comparacao',
        label: 'Foco da comparação',
        type: 'textarea',
        required: false,
        placeholder: 'Quais aspectos deseja comparar?'
      }
    ],
    order: 9
  },
  {
    id: 'criar_clausula',
    name: 'Criar Cláusula Contratual',
    description: 'Elaboração de cláusula específica',
    icon: '📌',
    category: 'contrato',
    area: 'geral',
    fields: [
      {
        id: 'tipo_clausula',
        name: 'tipo_clausula',
        label: 'Tipo de cláusula',
        type: 'text',
        required: true,
        placeholder: 'Ex: Cláusula de Confidencialidade, Cláusula Penal...'
      },
      {
        id: 'objetivo_clausula',
        name: 'objetivo_clausula',
        label: 'Objetivo da cláusula',
        type: 'textarea',
        required: true,
        placeholder: 'O que a cláusula deve prever?'
      }
    ],
    order: 10
  },
  {
    id: 'termos_uso',
    name: 'Termos de Uso',
    description: 'Elaboração de Termos de Uso para plataforma',
    icon: '📱',
    category: 'contrato',
    area: 'geral',
    fields: [
      {
        id: 'nome_empresa',
        name: 'nome_empresa',
        label: 'Nome da empresa/plataforma',
        type: 'text',
        required: true,
        placeholder: 'Ex: Minha Empresa Ltda.'
      },
      {
        id: 'tipo_servico',
        name: 'tipo_servico',
        label: 'Tipo de serviço/produto',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o tipo de serviço ou produto oferecido...'
      }
    ],
    order: 11
  },

  // ========== EXTRAJUDICIAL ==========
  {
    id: 'notificacao_extrajudicial',
    name: 'Notificação Extrajudicial',
    description: 'Comunicação formal antes do processo',
    icon: '📨',
    category: 'extrajudicial',
    area: 'geral',
    fields: [
      {
        id: 'destinatario',
        name: 'destinatario',
        label: 'Destinatário',
        type: 'text',
        required: true,
        placeholder: 'Nome do destinatário da notificação'
      },
      {
        id: 'objeto_notificacao',
        name: 'objeto_notificacao',
        label: 'Objeto da Notificação',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva o objeto da notificação...'
      },
      {
        id: 'prazo_resposta',
        name: 'prazo_resposta',
        label: 'Prazo para resposta',
        type: 'text',
        required: false,
        placeholder: 'Ex: 10 dias'
      },
      CAMPOS_PADRAO.documentosAnexos
    ],
    popular: true,
    order: 1
  },
  {
    id: 'contranotificacao_extrajudicial',
    name: 'Contranotificação Extrajudicial',
    description: 'Resposta a notificação extrajudicial',
    icon: '↩️',
    category: 'extrajudicial',
    area: 'geral',
    fields: [
      {
        id: 'notificacao_original',
        name: 'notificacao_original',
        label: 'Notificação original recebida',
        type: 'textarea',
        required: true,
        placeholder: 'Transcreva ou descreva a notificação recebida...'
      },
      {
        id: 'resposta',
        name: 'resposta',
        label: 'Argumentos da contranotificação',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva seus argumentos em resposta...'
      },
      CAMPOS_PADRAO.documentosAnexos
    ],
    popular: true,
    order: 2
  },
  {
    id: 'parecer_juridico',
    name: 'Parecer Jurídico',
    description: 'Opinião técnica sobre questão jurídica',
    icon: '📋',
    category: 'extrajudicial',
    area: 'geral',
    fields: [
      {
        id: 'consulta',
        name: 'consulta',
        label: 'Consulta',
        type: 'textarea',
        required: true,
        placeholder: 'Descreva a questão jurídica a ser analisada...'
      },
      CAMPOS_PADRAO.documentosAnexos,
      CAMPOS_PADRAO.estiloEscrita
    ],
    popular: true,
    order: 3
  },
  {
    id: 'procuracao',
    name: 'Procuração',
    description: 'Instrumento de mandato',
    icon: '📜',
    category: 'extrajudicial',
    area: 'geral',
    fields: [
      {
        id: 'outorgante',
        name: 'outorgante',
        label: 'Outorgante (quem confere poderes)',
        type: 'textarea',
        required: true,
        placeholder: 'Nome completo, nacionalidade, estado civil, profissão, CPF e endereço'
      },
      {
        id: 'outorgado',
        name: 'outorgado',
        label: 'Outorgado (quem recebe poderes)',
        type: 'textarea',
        required: true,
        placeholder: 'Nome completo, nacionalidade, estado civil, profissão, OAB e endereço'
      },
      {
        id: 'poderes',
        name: 'poderes',
        label: 'Poderes conferidos',
        type: 'select',
        required: true,
        options: [
          { value: 'geral', label: 'Poderes Gerais (Ad Judicia)' },
          { value: 'especial', label: 'Poderes Especiais' },
          { value: 'ambos', label: 'Poderes Gerais e Especiais' }
        ]
      }
    ],
    popular: true,
    order: 4
  },
  {
    id: 'inventario_extrajudicial',
    name: 'Petição de Inventário Extrajudicial',
    description: 'Inventário em cartório',
    icon: '📜',
    category: 'extrajudicial',
    area: 'familia',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 5
  },
  {
    id: 'acordo_alimentos_guarda',
    name: 'Acordo de Alimentos, Guarda e Visitas',
    description: 'Acordo extrajudicial de família',
    icon: '👨‍👩‍👧',
    category: 'extrajudicial',
    area: 'familia',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 6
  },
  {
    id: 'acordo_divorcio',
    name: 'Acordo de Divórcio, Partilha, Alimentos, Guarda e Visitas',
    description: 'Acordo completo de divórcio',
    icon: '👨‍👩‍👧',
    category: 'extrajudicial',
    area: 'familia',
    fields: CAMPOS_PETICAO_INICIAL,
    popular: true,
    order: 7
  },
  {
    id: 'habilitacao_credito_administrativa',
    name: 'Habilitação de Crédito Administrativa',
    description: 'Habilitação em liquidação extrajudicial de banco',
    icon: '🏦',
    category: 'extrajudicial',
    area: 'geral',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 9
  },
  {
    id: 'habilitacao_credito_retardataria',
    name: 'Habilitação de Crédito Retardatária',
    description: 'Habilitação após prazo em liquidação extrajudicial',
    icon: '🏦',
    category: 'extrajudicial',
    area: 'geral',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 10
  },
  {
    id: 'impugnacao_credito_liquidacao',
    name: 'Impugnação de Crédito em Liquidação Extrajudicial',
    description: 'Contestação de crédito em liquidação de banco',
    icon: '🏦',
    category: 'extrajudicial',
    area: 'geral',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 11
  },
  {
    id: 'testamento_particular',
    name: 'Testamento Particular',
    description: 'Disposição de última vontade escrita pelo testador',
    icon: '📜',
    category: 'extrajudicial',
    area: 'familia',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 12
  },

  // ========== TRABALHISTA - TIPOS ADICIONAIS ==========
  {
    id: 'emenda_inicial_trabalhista',
    name: 'Emenda à Inicial (Trabalhista)',
    description: 'Correção ou complementação da reclamação trabalhista',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 10
  },
  {
    id: 'manifestacao_trabalhista',
    name: 'Manifestação Processual (Trabalhista)',
    description: 'Manifestação genérica em processo trabalhista',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 11
  },
  {
    id: 'quesitos_periciais_trabalhista',
    name: 'Quesitos Periciais (Trabalhista)',
    description: 'Formulação de quesitos para perícia trabalhista',
    icon: '❓',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 12
  },
  {
    id: 'alegacoes_finais_trabalhista',
    name: 'Alegações Finais (Trabalhista)',
    description: 'Alegações finais em processo trabalhista',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 13
  },
  {
    id: 'embargos_declaracao_trabalhista',
    name: 'Embargos de Declaração (Trabalhista)',
    description: 'Embargos declaratórios em processo trabalhista',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 14
  },
  {
    id: 'agravo_instrumento_trabalhista',
    name: 'Agravo de Instrumento (Trabalhista)',
    description: 'Recurso contra decisão interlocutória trabalhista',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 15
  },
  {
    id: 'mandado_seguranca_trabalhista',
    name: 'Mandado de Segurança (Trabalhista)',
    description: 'MS contra ato de autoridade trabalhista',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 16
  },
  {
    id: 'impugnacao_liquidacao_trabalhista',
    name: 'Impugnação à Liquidação (Trabalhista)',
    description: 'Impugnação aos cálculos de liquidação',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 17
  },
  {
    id: 'excecao_incompetencia_trabalhista',
    name: 'Exceção de Incompetência (Trabalhista)',
    description: 'Arguição de incompetência do juízo trabalhista',
    icon: '⚠️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 18
  },
  {
    id: 'acao_cumprimento_trabalhista',
    name: 'Ação de Cumprimento (Trabalhista)',
    description: 'Ação para cumprimento de sentença normativa',
    icon: '✅',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 19
  },
  {
    id: 'habeas_corpus_trabalhista',
    name: 'Habeas Corpus (Trabalhista)',
    description: 'HC em matéria trabalhista',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 20
  },
  {
    id: 'agravo_regimental_trabalhista',
    name: 'Agravo Regimental (Trabalhista)',
    description: 'Agravo contra decisão monocrática',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 21
  },
  {
    id: 'embargos_sdi_tst',
    name: 'Embargos para SDI do TST',
    description: 'Embargos à SDI em divergência jurisprudencial',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 22
  },
  {
    id: 'memoriais_recursais_trabalhista',
    name: 'Memoriais Recursais (Trabalhista)',
    description: 'Memoriais para julgamento de recurso trabalhista',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 23
  },
  {
    id: 'recurso_extraordinario_trabalhista',
    name: 'Recurso Extraordinário (Trabalhista)',
    description: 'RE ao STF em matéria trabalhista',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 24
  },
  {
    id: 'contrarrazoes_re_trabalhista',
    name: 'Contrarrazões ao RE (Trabalhista)',
    description: 'Resposta ao RE trabalhista',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 25
  },
  {
    id: 'contraminuta_agravo_instrumento_trabalhista',
    name: 'Contraminuta ao Agravo de Instrumento (Trabalhista)',
    description: 'Resposta ao AI trabalhista',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 26
  },
  {
    id: 'acao_rescisoria_trabalhista',
    name: 'Ação Rescisória (Trabalhista)',
    description: 'Rescisória trabalhista',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 27
  },
  {
    id: 'cumprimento_sentenca_trabalhista',
    name: 'Cumprimento de Sentença (Trabalhista)',
    description: 'Execução de sentença trabalhista',
    icon: '✅',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 28
  },
  {
    id: 'impugnacao_cumprimento_sentenca_trabalhista',
    name: 'Impugnação ao Cumprimento de Sentença (Trabalhista)',
    description: 'Defesa ao cumprimento trabalhista',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 29
  },
  {
    id: 'embargos_execucao_trabalhista',
    name: 'Embargos à Execução (Trabalhista)',
    description: 'Defesa na execução trabalhista',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 30
  },
  {
    id: 'impugnacao_embargos_execucao_trabalhista',
    name: 'Impugnação aos Embargos à Execução (Trabalhista)',
    description: 'Resposta aos embargos trabalhistas',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 31
  },
  {
    id: 'contrarrazoes_agravo_peticao',
    name: 'Contrarrazões ao Agravo de Petição',
    description: 'Resposta ao agravo de petição',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 32
  },
  {
    id: 'recurso_ordinario_constitucional_trabalhista',
    name: 'Recurso Ordinário Constitucional (Trabalhista)',
    description: 'ROC ao TST ou STF',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 33
  },
  {
    id: 'cumprimento_provisorio_sentenca_trabalhista',
    name: 'Cumprimento Provisório de Sentença (Trabalhista)',
    description: 'Execução provisória trabalhista',
    icon: '⏳',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 34
  },
  {
    id: 'impugnacao_homologacao_calculos_trabalhista',
    name: 'Impugnação à Homologação dos Cálculos (Trabalhista)',
    description: 'Impugnação aos cálculos homologados',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 35
  },
  {
    id: 'embargos_monitoria_trabalhista',
    name: 'Embargos à Ação Monitória (Trabalhista)',
    description: 'Defesa na monitória trabalhista',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 36
  },
  {
    id: 'pedido_penhora_faturamento_trabalhista',
    name: 'Pedido de Penhora de Faturamento (Trabalhista)',
    description: 'Penhora sobre faturamento',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 37
  },
  {
    id: 'pedido_penhora_rosto_autos_trabalhista',
    name: 'Pedido de Penhora no Rosto dos Autos (Trabalhista)',
    description: 'Penhora de crédito em outro processo',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 38
  },
  {
    id: 'pedido_expedicao_rpv_trabalhista',
    name: 'Pedido de Expedição de RPV (Trabalhista)',
    description: 'Requisição de Pequeno Valor',
    icon: '💵',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 39
  },
  {
    id: 'pedido_revisao_trabalhista',
    name: 'Pedido de Revisão (Trabalhista)',
    description: 'Revisão de valor ou decisão',
    icon: '🔄',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_CONTESTACAO,
    order: 40
  },
  {
    id: 'contraminuta_agravo_interno_trabalhista',
    name: 'Contraminuta ao Agravo Interno (Trabalhista)',
    description: 'Resposta ao agravo interno',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 41
  },
  {
    id: 'recurso_adesivo',
    name: 'Recurso Adesivo (Trabalhista)',
    description: 'Recurso adesivo ao recurso da parte contrária',
    icon: '📎',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_RECURSO,
    order: 42
  },
  {
    id: 'consignacao_pagamento_trabalhista',
    name: 'Ação de Consignação em Pagamento (Trabalhista)',
    description: 'Consignação de verbas trabalhistas',
    icon: '💵',
    category: 'pecas_juridicas',
    area: 'trabalhista',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 43
  },

  // ========== CRIMINAL/PENAL - TIPOS ADICIONAIS ==========
  {
    id: 'revogacao_prisao_preventiva',
    name: 'Pedido de Revogação de Prisão Preventiva',
    description: 'Revogação da prisão preventiva',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 11
  },
  {
    id: 'revogacao_prisao_medidas',
    name: 'Revogação de Prisão com Medidas Cautelares',
    description: 'Substituição da prisão por cautelares',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 12
  },
  {
    id: 'denuncia',
    name: 'Denúncia',
    description: 'Peça acusatória do MP',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 13
  },
  {
    id: 'emenda_inicial_criminal',
    name: 'Emenda à Inicial (Criminal)',
    description: 'Correção da denúncia ou queixa',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 14
  },
  {
    id: 'replica_criminal',
    name: 'Réplica Criminal (Alegações do MP)',
    description: 'Manifestação do MP à defesa prévia',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 15
  },
  {
    id: 'excecao_suspeicao_criminal',
    name: 'Exceção de Suspeição (Criminal)',
    description: 'Arguição de suspeição do juiz',
    icon: '⚠️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 16
  },
  {
    id: 'relaxamento_prisao',
    name: 'Pedido de Relaxamento de Prisão',
    description: 'Relaxamento de prisão ilegal',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 17
  },
  {
    id: 'revogacao_medidas_protetivas',
    name: 'Revogação de Medidas Protetivas',
    description: 'Revogação de medidas da Lei Maria da Penha',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 18
  },
  {
    id: 'quesitos_periciais_criminal',
    name: 'Quesitos Periciais (Criminal)',
    description: 'Quesitos para perícia criminal',
    icon: '❓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 19
  },
  {
    id: 'manifestacao_processual_criminal',
    name: 'Manifestação Processual (Criminal)',
    description: 'Manifestação genérica criminal',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 20
  },
  {
    id: 'contrarrazoes_apelacao_criminal',
    name: 'Contrarrazões à Apelação Criminal',
    description: 'Resposta à apelação criminal',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 21
  },
  {
    id: 'contrarrazoes_rese',
    name: 'Contrarrazões ao RESE',
    description: 'Resposta ao Recurso em Sentido Estrito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 22
  },
  {
    id: 'recurso_ordinario_constitucional_criminal',
    name: 'ROC Criminal',
    description: 'Recurso Ordinário Constitucional Criminal',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 23
  },
  {
    id: 'memoriais_recursais_criminal',
    name: 'Memoriais Recursais (Criminal)',
    description: 'Memoriais para recurso criminal',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 24
  },
  {
    id: 'memoriais_stj_criminal',
    name: 'Memoriais STJ (Criminal)',
    description: 'Memoriais ao Ministro do STJ',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 25
  },
  {
    id: 'recurso_especial_criminal',
    name: 'Recurso Especial (Criminal)',
    description: 'REsp em matéria criminal',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 26
  },
  {
    id: 'contrarrazoes_resp_criminal',
    name: 'Contrarrazões ao REsp (Criminal)',
    description: 'Resposta ao REsp criminal',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 27
  },
  {
    id: 'agravo_resp_criminal',
    name: 'Agravo em REsp (Criminal)',
    description: 'Agravo contra inadmissão de REsp',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 28
  },
  {
    id: 'contraminuta_agravo_resp_criminal',
    name: 'Contraminuta ao Agravo em REsp (Criminal)',
    description: 'Resposta ao agravo em REsp',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 29
  },
  {
    id: 'recurso_extraordinario_criminal',
    name: 'Recurso Extraordinário (Criminal)',
    description: 'RE ao STF em matéria criminal',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 30
  },
  {
    id: 'contrarrazoes_re_criminal',
    name: 'Contrarrazões ao RE (Criminal)',
    description: 'Resposta ao RE criminal',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 31
  },
  {
    id: 'embargos_declaracao_criminal',
    name: 'Embargos de Declaração (Criminal)',
    description: 'Embargos declaratórios criminais',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 32
  },
  {
    id: 'contrarrazoes_embargos_declaracao_criminal',
    name: 'Contrarrazões aos Embargos (Criminal)',
    description: 'Resposta aos embargos criminais',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 33
  },
  {
    id: 'restituicao_coisa_apreendida',
    name: 'Restituição de Coisa Apreendida',
    description: 'Pedido de restituição de bens',
    icon: '📦',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 34
  },
  {
    id: 'peticao_incidente_execucao',
    name: 'Petição de Incidente (Execução Penal)',
    description: 'Incidente na execução penal',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 35
  },
  {
    id: 'agravo_execucao_penal',
    name: 'Agravo em Execução Penal',
    description: 'Agravo contra decisão da execução',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 36
  },
  {
    id: 'contrarrazoes_agravo_execucao',
    name: 'Contrarrazões ao Agravo em Execução',
    description: 'Resposta ao agravo em execução',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_RECURSO,
    order: 37
  },
  {
    id: 'livramento_condicional',
    name: 'Pedido de Livramento Condicional',
    description: 'Livramento condicional do apenado',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 38
  },
  {
    id: 'indulto_natalino',
    name: 'Pedido de Indulto Natalino',
    description: 'Indulto com base em decreto',
    icon: '🎄',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 39
  },
  {
    id: 'peca_modelo_criminal',
    name: 'Peça a partir de Modelo (Criminal)',
    description: 'Adaptar modelo de peça criminal',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'criminal',
    fields: CAMPOS_CONTESTACAO,
    order: 40
  },

  // ========== PREVIDENCIÁRIO - TIPOS ADICIONAIS ==========
  {
    id: 'requerimento_beneficio',
    name: 'Requerimento de Benefício',
    description: 'Requerimento administrativo ao INSS',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 5
  },
  {
    id: 'emenda_inicial_previdenciario',
    name: 'Emenda à Inicial (Previdenciário)',
    description: 'Correção da inicial previdenciária',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 6
  },
  {
    id: 'contestacao_previdenciario',
    name: 'Contestação (Previdenciário)',
    description: 'Defesa do INSS',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 7
  },
  {
    id: 'replica_previdenciario',
    name: 'Réplica (Previdenciário)',
    description: 'Resposta à contestação do INSS',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 8
  },
  {
    id: 'manifestacao_previdenciario',
    name: 'Manifestação (Previdenciário)',
    description: 'Manifestação processual previdenciária',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 9
  },
  {
    id: 'producao_provas_previdenciario',
    name: 'Produção de Provas (Previdenciário)',
    description: 'Requerimento de provas previdenciárias',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 10
  },
  {
    id: 'quesitos_periciais_previdenciario',
    name: 'Quesitos Periciais (Previdenciário)',
    description: 'Quesitos para perícia médica',
    icon: '❓',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 11
  },
  {
    id: 'alegacoes_finais_previdenciario',
    name: 'Alegações Finais (Previdenciário)',
    description: 'Alegações finais previdenciárias',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 12
  },
  {
    id: 'recurso_inominado_previdenciario',
    name: 'Recurso Inominado (Previdenciário)',
    description: 'Recurso ao JEF previdenciário',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 13
  },
  {
    id: 'apelacao_previdenciario',
    name: 'Apelação (Previdenciário)',
    description: 'Apelação previdenciária',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 14
  },
  {
    id: 'contrarrazoes_apelacao_previdenciario',
    name: 'Contrarrazões à Apelação (Previdenciário)',
    description: 'Resposta à apelação do INSS',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 15
  },
  {
    id: 'agravo_instrumento_previdenciario',
    name: 'Agravo de Instrumento (Previdenciário)',
    description: 'Agravo contra decisão interlocutória',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 16
  },
  {
    id: 'contraminuta_agravo_previdenciario',
    name: 'Contraminuta ao AI (Previdenciário)',
    description: 'Resposta ao agravo de instrumento',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 17
  },
  {
    id: 'agravo_interno_previdenciario',
    name: 'Agravo Interno (Previdenciário)',
    description: 'Agravo interno previdenciário',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 18
  },
  {
    id: 'contraminuta_agravo_interno_previdenciario',
    name: 'Contraminuta ao Agravo Interno (Previdenciário)',
    description: 'Resposta ao agravo interno',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 19
  },
  {
    id: 'cumprimento_sentenca_previdenciario',
    name: 'Cumprimento de Sentença (Previdenciário)',
    description: 'Execução de sentença previdenciária',
    icon: '✅',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 20
  },
  {
    id: 'impugnacao_cumprimento_previdenciario',
    name: 'Impugnação ao Cumprimento (Previdenciário)',
    description: 'Defesa ao cumprimento previdenciário',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 21
  },
  {
    id: 'impugnacao_homologacao_previdenciario',
    name: 'Impugnação à Homologação (Previdenciário)',
    description: 'Impugnação aos cálculos homologados',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 22
  },
  {
    id: 'memoriais_previdenciario',
    name: 'Memoriais (Previdenciário)',
    description: 'Memoriais recursais previdenciários',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 23
  },
  {
    id: 'memoriais_stj_previdenciario',
    name: 'Memoriais STJ (Previdenciário)',
    description: 'Memoriais ao Ministro do STJ',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 24
  },
  {
    id: 'recurso_especial_previdenciario_judicial',
    name: 'Recurso Especial Judicial (Previdenciário)',
    description: 'REsp em matéria previdenciária',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 25
  },
  {
    id: 'contrarrazoes_resp_previdenciario',
    name: 'Contrarrazões ao REsp (Previdenciário)',
    description: 'Resposta ao REsp previdenciário',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 26
  },
  {
    id: 'recurso_extraordinario_previdenciario',
    name: 'Recurso Extraordinário (Previdenciário)',
    description: 'RE ao STF em matéria previdenciária',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 27
  },
  {
    id: 'contrarrazoes_re_previdenciario',
    name: 'Contrarrazões ao RE (Previdenciário)',
    description: 'Resposta ao RE previdenciário',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 28
  },
  {
    id: 'agravo_resp_previdenciario',
    name: 'Agravo em REsp (Previdenciário)',
    description: 'Agravo contra inadmissão de REsp',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 29
  },
  {
    id: 'contraminuta_agravo_resp_previdenciario',
    name: 'Contraminuta ao Agravo em REsp (Previdenciário)',
    description: 'Resposta ao agravo em REsp',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 30
  },
  {
    id: 'recurso_ordinario_constitucional_previdenciario',
    name: 'ROC (Previdenciário)',
    description: 'Recurso Ordinário Constitucional',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_RECURSO,
    order: 31
  },
  {
    id: 'mandado_seguranca_previdenciario',
    name: 'Mandado de Segurança (Previdenciário)',
    description: 'MS contra ato do INSS',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 32
  },
  {
    id: 'rpv_previdenciario',
    name: 'RPV (Previdenciário)',
    description: 'Requisição de Pequeno Valor',
    icon: '💵',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 33
  },
  {
    id: 'peca_modelo_previdenciario',
    name: 'Peça a partir de Modelo (Previdenciário)',
    description: 'Adaptar modelo previdenciário',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'previdenciario',
    fields: CAMPOS_CONTESTACAO,
    order: 34
  },

  // ========== TRÂNSITO - TIPOS ADICIONAIS ==========
  {
    id: 'manifestacao_transito',
    name: 'Manifestação (Trânsito)',
    description: 'Manifestação em processo de trânsito',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 4
  },
  {
    id: 'peticao_inicial_transito',
    name: 'Petição Inicial (Trânsito)',
    description: 'Ação judicial de trânsito',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 5
  },
  {
    id: 'contraminuta_agravo_transito',
    name: 'Contraminuta ao AI (Trânsito)',
    description: 'Resposta ao agravo de instrumento',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 6
  },
  {
    id: 'contrarrazoes_resp_transito',
    name: 'Contrarrazões ao REsp (Trânsito)',
    description: 'Resposta ao REsp de trânsito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 7
  },
  {
    id: 'contrarrazoes_re_transito',
    name: 'Contrarrazões ao RE (Trânsito)',
    description: 'Resposta ao RE de trânsito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 8
  },
  {
    id: 'contrarrazoes_ed_transito',
    name: 'Contrarrazões aos ED (Trânsito)',
    description: 'Resposta aos embargos de trânsito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 9
  },
  {
    id: 'contrarrazoes_apelacao_transito',
    name: 'Contrarrazões à Apelação (Trânsito)',
    description: 'Resposta à apelação de trânsito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 10
  },
  {
    id: 'embargos_declaracao_transito',
    name: 'Embargos de Declaração (Trânsito)',
    description: 'Embargos declaratórios de trânsito',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 11
  },
  {
    id: 'embargos_terceiro_transito',
    name: 'Embargos de Terceiro (Trânsito)',
    description: 'Defesa de terceiro em trânsito',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 12
  },
  {
    id: 'excecao_pre_executividade_transito',
    name: 'Exceção de Pré-executividade (Trânsito)',
    description: 'Defesa na execução fiscal de multas',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 13
  },
  {
    id: 'replica_transito',
    name: 'Réplica (Trânsito)',
    description: 'Resposta à contestação de trânsito',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 14
  },
  {
    id: 'impugnacao_cumprimento_transito',
    name: 'Impugnação ao Cumprimento (Trânsito)',
    description: 'Defesa ao cumprimento de trânsito',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 15
  },
  {
    id: 'impugnacao_embargos_execucao_transito',
    name: 'Impugnação aos Embargos (Trânsito)',
    description: 'Resposta aos embargos de execução',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 16
  },
  {
    id: 'impugnacao_homologacao_transito',
    name: 'Impugnação à Homologação (Trânsito)',
    description: 'Impugnação aos cálculos de trânsito',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 17
  },
  {
    id: 'execucao_titulo_transito',
    name: 'Execução de Título (Trânsito)',
    description: 'Execução extrajudicial de trânsito',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 18
  },
  {
    id: 'mandado_seguranca_transito',
    name: 'Mandado de Segurança (Trânsito)',
    description: 'MS contra autoridade de trânsito',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 19
  },
  {
    id: 'manifestacao_impugnacao_embargos_transito',
    name: 'Manifestação à Impugnação (Trânsito)',
    description: 'Resposta à impugnação de embargos',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 20
  },
  {
    id: 'producao_provas_transito',
    name: 'Produção de Provas (Trânsito)',
    description: 'Requerimento de provas de trânsito',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 21
  },
  {
    id: 'recurso_especial_transito',
    name: 'Recurso Especial (Trânsito)',
    description: 'REsp em matéria de trânsito',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 22
  },
  {
    id: 'recurso_extraordinario_transito',
    name: 'Recurso Extraordinário (Trânsito)',
    description: 'RE ao STF em matéria de trânsito',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 23
  },
  {
    id: 'recurso_inominado_transito',
    name: 'Recurso Inominado (Trânsito)',
    description: 'Recurso ao JEF em trânsito',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 24
  },
  {
    id: 'agravo_instrumento_transito',
    name: 'Agravo de Instrumento (Trânsito)',
    description: 'Agravo contra decisão de trânsito',
    icon: '📊',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 25
  },
  {
    id: 'quesitos_periciais_transito',
    name: 'Quesitos Periciais (Trânsito)',
    description: 'Quesitos para perícia de trânsito',
    icon: '❓',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 26
  },
  {
    id: 'memoriais_transito',
    name: 'Memoriais (Trânsito)',
    description: 'Memoriais recursais de trânsito',
    icon: '📝',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 27
  },
  {
    id: 'peca_modelo_transito',
    name: 'Peça a partir de Modelo (Trânsito)',
    description: 'Adaptar modelo de trânsito',
    icon: '📋',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_CONTESTACAO,
    order: 28
  },
  {
    id: 'recurso_ordinario_constitucional_transito',
    name: 'ROC (Trânsito)',
    description: 'Recurso Ordinário Constitucional',
    icon: '🏛️',
    category: 'pecas_juridicas',
    area: 'transito',
    fields: CAMPOS_RECURSO,
    order: 29
  },

  // ========== MAGISTRATURA ==========
  {
    id: 'acordao',
    name: 'Acórdão',
    description: 'Decisão colegiada de tribunal',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    order: 1
  },
  {
    id: 'decisao_judicial',
    name: 'Decisão Judicial',
    description: 'Decisão interlocutória ou monocrática',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    order: 2
  },
  {
    id: 'sentenca',
    name: 'Sentença',
    description: 'Decisão que resolve o mérito',
    icon: '⚖️',
    category: 'pecas_juridicas',
    area: 'magistratura',
    fields: CAMPOS_CONTESTACAO,
    order: 3
  },

  // ========== RECESSO ==========
  {
    id: 'indulto_natalino_recesso',
    name: 'Pedido de Indulto Natalino',
    description: 'Indulto em período de recesso',
    icon: '🎄',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 1
  },
  {
    id: 'sustacao_protesto_recesso',
    name: 'Sustação de Protesto (Recesso)',
    description: 'Medida urgente contra protesto',
    icon: '🚫',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 2
  },
  {
    id: 'busca_apreensao_menor_recesso',
    name: 'Busca e Apreensão de Menor (Recesso)',
    description: 'Medida urgente para menor',
    icon: '👶',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 3
  },
  {
    id: 'contranotificacao',
    name: 'Contranotificação Extrajudicial',
    description: 'Resposta a notificação',
    icon: '↩️',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 4
  },
  {
    id: 'habeas_corpus_recesso',
    name: 'Habeas Corpus (Recesso)',
    description: 'HC em período de plantão',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 5
  },
  {
    id: 'impugnacao_penhora_recesso',
    name: 'Impugnação à Penhora (Recesso)',
    description: 'Defesa contra penhora urgente',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 6
  },
  {
    id: 'adjudicacao_compulsoria_recesso',
    name: 'Adjudicação Compulsória (Recesso)',
    description: 'Ação para obter escritura',
    icon: '🏠',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 7
  },
  {
    id: 'mandado_seguranca_recesso',
    name: 'Mandado de Segurança (Recesso)',
    description: 'MS em período de plantão',
    icon: '🛡️',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 8
  },
  {
    id: 'manifestacao_recesso',
    name: 'Manifestação Processual (Recesso)',
    description: 'Manifestação urgente',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 9
  },
  {
    id: 'cumprimento_provisorio_recesso',
    name: 'Cumprimento Provisório (Recesso)',
    description: 'Execução provisória urgente',
    icon: '⏳',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 10
  },
  {
    id: 'rpv_recesso',
    name: 'RPV (Recesso)',
    description: 'Requisição de Pequeno Valor',
    icon: '💵',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 11
  },
  {
    id: 'levantamento_valores',
    name: 'Pedido de Levantamento de Valores',
    description: 'Saque de valores judiciais',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 12
  },
  {
    id: 'liberdade_provisoria_recesso',
    name: 'Liberdade Provisória (Recesso)',
    description: 'Pedido de liberdade em plantão',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 13
  },
  {
    id: 'penhora_faturamento',
    name: 'Pedido de Penhora de Faturamento',
    description: 'Penhora sobre faturamento',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 14
  },
  {
    id: 'penhora_rosto_autos',
    name: 'Penhora no Rosto dos Autos',
    description: 'Penhora de crédito em outro processo',
    icon: '💰',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 15
  },
  {
    id: 'relaxamento_prisao_recesso',
    name: 'Relaxamento de Prisão (Recesso)',
    description: 'Relaxamento em período de plantão',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 16
  },
  {
    id: 'revogacao_preventiva_recesso',
    name: 'Revogação de Preventiva (Recesso)',
    description: 'Revogação em período de plantão',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 17
  },
  {
    id: 'revogacao_preventiva_medidas_recesso',
    name: 'Revogação com Cautelares (Recesso)',
    description: 'Substituição por cautelares em plantão',
    icon: '🔓',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_HABEAS_CORPUS,
    order: 18
  },
  {
    id: 'peticao_incidente_recesso',
    name: 'Petição de Incidente (Recesso)',
    description: 'Incidente urgente em execução',
    icon: '📄',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 19
  },
  {
    id: 'tutela_antecipada_antecedente',
    name: 'Tutela Antecipada Antecedente',
    description: 'Tutela de urgência antes da ação',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 20
  },
  {
    id: 'tutela_cautelar_antecedente',
    name: 'Tutela Cautelar Antecedente',
    description: 'Tutela cautelar antes da ação',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_PETICAO_INICIAL,
    order: 21
  },
  {
    id: 'tutela_antecipada_incidental',
    name: 'Tutela Antecipada Incidental',
    description: 'Tutela de urgência no processo',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 22
  },
  {
    id: 'tutela_evidencia_recesso',
    name: 'Tutela da Evidência (Recesso)',
    description: 'Tutela baseada em evidência',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 23
  },
  {
    id: 'tutela_cautelar_incidental',
    name: 'Tutela Cautelar Incidental',
    description: 'Tutela cautelar no processo',
    icon: '⚡',
    category: 'pecas_juridicas',
    area: 'recesso',
    fields: CAMPOS_CONTESTACAO,
    order: 24
  }
];

// ==========================================
// HELPERS
// ==========================================

/**
 * Retorna tipos de documentos filtrados por categoria
 */
export const getTypesByCategory = (category: DocumentCategory): DocumentType[] => {
  return DOCUMENT_TYPES
    .filter(t => t.category === category)
    .sort((a, b) => (a.order || 999) - (b.order || 999));
};

/**
 * Retorna tipos de documentos filtrados por área
 */
export const getTypesByArea = (area: DocumentArea): DocumentType[] => {
  return DOCUMENT_TYPES
    .filter(t => t.area === area)
    .sort((a, b) => (a.order || 999) - (b.order || 999));
};

/**
 * Retorna tipos de documentos filtrados por categoria e área
 */
export const getTypesByCategoryAndArea = (category: DocumentCategory, area: DocumentArea): DocumentType[] => {
  return DOCUMENT_TYPES
    .filter(t => t.category === category && t.area === area)
    .sort((a, b) => (a.order || 999) - (b.order || 999));
};

/**
 * Retorna tipos de documentos populares ("mais utilizados")
 */
export const getPopularTypes = (): DocumentType[] => {
  return DOCUMENT_TYPES
    .filter(t => t.popular)
    .sort((a, b) => (a.order || 999) - (b.order || 999));
};

/**
 * Busca tipos de documentos por nome
 */
export const searchDocumentTypes = (query: string): DocumentType[] => {
  const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  return DOCUMENT_TYPES.filter(t => {
    const normalizedName = t.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const normalizedDesc = (t.description || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return normalizedName.includes(normalizedQuery) || normalizedDesc.includes(normalizedQuery);
  });
};

/**
 * Retorna um tipo de documento pelo ID
 */
export const getDocumentTypeById = (id: string): DocumentType | undefined => {
  return DOCUMENT_TYPES.find(t => t.id === id);
};

/**
 * Retorna configuração de categoria pelo ID
 */
export const getCategoryConfig = (id: DocumentCategory): CategoryConfig | undefined => {
  return CATEGORIES.find(c => c.id === id);
};

/**
 * Retorna configuração de área pelo ID
 */
export const getAreaConfig = (id: DocumentArea): AreaConfig | undefined => {
  return AREAS.find(a => a.id === id);
};

/**
 * Retorna áreas disponíveis para uma categoria
 */
export const getAreasForCategory = (category: DocumentCategory): AreaConfig[] => {
  const areasInCategory = new Set(
    DOCUMENT_TYPES
      .filter(t => t.category === category)
      .map(t => t.area)
  );
  
  return AREAS.filter(a => areasInCategory.has(a.id));
};

export default DOCUMENT_TYPES;

